/**
 * Web 端诊断日志收集器（003 方案「前端诊断日志上报中心」，对齐 ai_mobile 048/003 同构实现）。
 *
 * 定位：ai_web（PC 端商城）此前没有任何前端排障手段，线上问题只能靠用户
 * 口头描述。本收集器与 mobile 端（src/utils/diagCollector.ts）结构同构：
 * 纯内存白名单日志收集 + 右上角悬浮按钮，一键上报入库（admin 可预览/下载）。
 *
 * 激活方式（两种来源，以远程开关为主）：
 * 1. URL 带 ?diag=1：研发取证用（本地导出 JSON，生产零感知）；
 * 2. 远程开关（SystemConfig key: diag_log_center 的 web 字段）：admin
 *    「开发相关 → 诊断日志」页开启后，页面右上角出现"上报日志"按钮。
 *
 * 关闭态零感知（003 文档 4.0 硬性验收标准）：
 * - 开关关闭且无 URL 参数 → init 早退：不 hook console、不建缓冲、无任何 UI；
 * - 开关查询在页面加载后空闲时机发起（requestIdleCallback，降级 setTimeout 2s），
 *   不阻塞渲染、不与首屏关键请求竞争；localStorage 缓存 TTL 5 分钟，
 *   TTL 内的后续启动零请求；
 * - 查询失败静默按"关闭"处理（不抛错、不打 console——诊断功能自身绝不
 *   产生日志噪音）；
 * - visibilitychange 回前台且缓存过期时后台重查（admin 开/关开关后，
 *   用户最多 5 分钟内按钮自动出现/摘除，无需刷新）；
 * - 中途 on→off：摘除按钮、停止收集（console hook 保留为纯透传——
 *   运行时摘除会破坏与其他 console 包装器的包装链，纯透传零开销且安全）。
 *
 * 收集内容（web 端数据源）：
 * 1. 白名单 console 日志：`[Http]`（http.js / ai-plugin-http.ts 拦截器的
 *    请求/响应错误埋点）+ 预留语音链路前缀（[Tts]/[AudioManager]/
 *    [AudioPlayer]/[Iat]，web 端未来接入语音时直接复用）；
 * 2. mark() 结构化埋点入口：业务侧需要精确 ts 的关键点（当前 web 端
 *    暂无调用方，接口保留与 mobile 对齐）。
 *
 * 导出/上报方式：
 * - 远程开关模式：右上角"上报日志"按钮 → POST /ai-api/api/ai/diag/upload
 *   入库（admin 可预览/下载）；上传失败（网络/超限）自动降级为本地导出；
 * - URL 参数模式：右上角"导出诊断"按钮 → 剪贴板 + 下载 JSON 双通道，
 *   文件发回离线分析。
 */

/** 白名单 console 前缀：只收诊断相关埋点，避免全量收集撑爆内存 */
const CONSOLE_WHITELIST_PREFIXES = [
    '[Tts]',
    '[AudioManager]',
    '[AudioPlayer]',
    '[Iat]',
    '[Http]',
    '[Diag]',
]

/** 缓冲上限（条）：超出后丢最旧 */
const MAX_ENTRIES = 3000

/** 单条日志截断长度（字符）：防超长对象序列化撑爆导出体积 */
const MAX_MSG_LEN = 500

/** 悬浮导出按钮 / 结果提示的 z-index：高于业务浮层，确保任何浮层下可见 */
const DIAG_Z_INDEX = 10086

/** 远程开关查询地址（与后端 system_config_router 挂载对齐：/api/ai/system_config/diag_log；/ai-api 为 nginx 同域代理前缀，dev 走 vite 代理） */
const DIAG_SWITCH_URL = '/ai-api/api/ai/system_config/diag_log'

/** 诊断日志上传地址（与后端 diag_router 挂载对齐：/api/ai/diag/upload） */
const DIAG_UPLOAD_URL = '/ai-api/api/ai/diag/upload'

/** 远程开关 localStorage 缓存 key（TTL 内的后续启动零请求；与 mobile 端独立域名/缓存，互不影响） */
const DIAG_SWITCH_CACHE_KEY = 'glx_diag_switch'

/** 开关缓存 TTL（毫秒）：admin 开/关开关后按钮出现/摘除的最大延迟 */
const DIAG_SWITCH_CACHE_TTL = 5 * 60 * 1000

/** 开关查询延迟（毫秒）：requestIdleCallback 不可用时的降级时机 */
const DIAG_SWITCH_FALLBACK_DELAY = 2000

/** 服务端体积上限（与后端 DIAG_MAX_CONTENT_BYTES 对齐），客户端预裁剪避免白跑一趟 */
const DIAG_UPLOAD_MAX_BYTES = 1024 * 1024

interface DiagEntry {
    /** 相对收集器激活时刻的毫秒数（performance.now 基准，单调递增、不受系统时间跳变影响） */
    t: number
    /** 来源：console 白名单日志 / mark 结构化埋点 */
    src: 'console' | 'mark'
    msg: string
}

/** 激活来源：URL 参数（研发取证，不可被远程开关关闭）/ 远程开关（admin 控制） */
type DiagSource = 'url' | 'remote'

class DiagCollector {
    /** 是否已激活。未激活时所有 mark() 均为 no-op */
    enabled = false

    /** 激活来源（url 模式忽略远程开关的关闭，研发显式意图优先） */
    private source: DiagSource = 'url'

    private entries: DiagEntry[] = []
    /** 收集器激活时刻（performance.now），所有条目时间戳以此为 0 点 */
    private t0 = 0
    /** 激活时刻的 ISO 时间（导出 meta 用，与 t 相互对照还原绝对时间） */
    private startedAtIso = ''

    /** 是否已注入悬浮按钮（挂载幂等标志） */
    private _buttonMounted = false
    /** 悬浮按钮 DOM 引用（远程开关关闭时摘除） */
    private _buttonEl: HTMLDivElement | null = null

    /** 远程开关查询是否进行中（防 visibilitychange 重入并发） */
    private _switchFetching = false
    /** 上次开关查询时刻（毫秒），TTL 节流用 */
    private _switchFetchedAt = 0
    /** visibilitychange 监听是否已注册（幂等标志） */
    private _visibilityHooked = false

    /**
     * 初始化：main.tsx 最早调用（早于 React 挂载，保证 hook console 早于业务日志）。
     * - URL 带 ?diag=1：立即激活（本地导出模式）；
     * - 否则：调度远程开关检查（空闲时机），开关开启后激活（上报模式）。
     * 关闭态完全 no-op（003 文档 4.0：无 hook、无缓冲、无 UI）。
     */
    init(): void {
        if (this.enabled) {
            return
        }
        if (new URLSearchParams(window.location.search).get('diag') === '1') {
            this._activate('url')
            return
        }
        // 无 URL 参数：调度远程开关检查（失败静默按关闭处理）
        this._scheduleRemoteCheck()
        this._hookVisibility()
    }

    /**
     * 结构化埋点入口（未激活 no-op，业务侧零成本调用）。
     * @param tag  事件标签
     * @param data 附加数据（JSON 序列化后并入 msg）
     */
    mark(tag: string, data?: Record<string, unknown>): void {
        if (!this.enabled) {
            return
        }
        let msg = tag
        if (data) {
            try {
                msg += ' ' + JSON.stringify(data)
            } catch {
                // 序列化失败（如循环引用）只记 tag，不影响业务
            }
        }
        this._push('mark', msg)
    }

    /**
     * 导出全部收集数据为 JSON 字符串。
     * meta 含 UA / 页面地址 / 起止时间 / 屏幕信息（定位设备与采集窗口）。
     */
    exportDiag(): string {
        return JSON.stringify({
            meta: {
                ua: navigator.userAgent,
                href: location.href,
                startedAt: this.startedAtIso,
                exportedAt: new Date().toISOString(),
                screen: `${window.screen.width}x${window.screen.height}`,
                dpr: window.devicePixelRatio,
                count: this.entries.length,
            },
            events: this.entries,
        })
    }

    // ---------- 激活 / 去激活 ----------

    /** 激活（幂等）：hook console + 挂载按钮。t0 首次激活时锚定，重启后沿用（时间线连续）。 */
    private _activate(source: DiagSource): void {
        if (this.enabled) {
            return
        }
        this.enabled = true
        this.source = source
        if (!this.t0) {
            this.t0 = performance.now()
            this.startedAtIso = new Date().toISOString()
        }
        this._hookConsole()
        this._mountButton()
        // 走白名单日志（[Diag] 前缀）：激活确认行，导出后可核对采集窗口
        console.log(`[Diag] 003 web 诊断收集器已激活（来源=${source}；右上角按钮${source === 'remote' ? '上报日志' : '导出诊断'}）`)
    }

    /**
     * 去激活（仅远程来源）：摘除按钮、停止收集。
     * console hook 保留为纯透传（collect 入口检查 enabled，不再入缓冲）——
     * 运行时摘除 hook 会破坏与其他 console 包装器的包装链，纯透传零开销且安全。
     */
    private _deactivate(): void {
        if (!this.enabled || this.source === 'url') {
            return
        }
        this.enabled = false
        if (this._buttonEl) {
            this._buttonEl.remove()
            this._buttonEl = null
        }
        this._buttonMounted = false
    }

    // ---------- 远程开关（003 方案：关闭态零感知的调度链路） ----------

    /** 页面加载后空闲时机发起开关查询（不阻塞渲染、不与首屏请求竞争） */
    private _scheduleRemoteCheck(): void {
        const run = (): void => {
            void this._checkRemoteSwitch(true)
        }
        if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(run, { timeout: DIAG_SWITCH_FALLBACK_DELAY })
        } else {
            setTimeout(run, DIAG_SWITCH_FALLBACK_DELAY)
        }
    }

    /**
     * 查询远程开关并按需激活/去激活。
     * @param allowCache TTL 内直接用缓存（本次启动的首次查询强制走缓存优先）
     */
    private async _checkRemoteSwitch(allowCache: boolean): Promise<void> {
        if (this._switchFetching) {
            return
        }
        // URL 参数模式不受远程开关控制（研发显式意图）
        if (this.enabled && this.source === 'url') {
            return
        }
        const now = Date.now()
        if (allowCache && now - this._switchFetchedAt < DIAG_SWITCH_CACHE_TTL) {
            return
        }
        // TTL 内的缓存（本次启动已查过或上次会话留下的）
        const cached = this._readSwitchCache()
        if (cached && now - cached.ts < DIAG_SWITCH_CACHE_TTL) {
            if (cached.on) {
                this._activate('remote')
            } else {
                this._deactivate()
            }
            return
        }
        this._switchFetching = true
        try {
            const resp = await fetch(DIAG_SWITCH_URL, { credentials: 'include' })
            if (!resp.ok) {
                // 失败静默按"关闭"处理：不抛错、不打 console（诊断功能自身零噪音）
                return
            }
            const json = (await resp.json()) as { success?: boolean; data?: { web?: boolean } }
            const on = json?.success === true && json.data?.web === true
            this._writeSwitchCache(on)
            if (on) {
                this._activate('remote')
            } else {
                this._deactivate()
            }
        } catch {
            // 网络异常等：静默按"关闭"处理，下次 visibilitychange 重试
        } finally {
            this._switchFetching = false
            this._switchFetchedAt = Date.now()
        }
    }

    /** visibilitychange 回前台时重查（TTL 节流）——admin 开/关开关后按钮 ≤5 分钟自动生效 */
    private _hookVisibility(): void {
        if (this._visibilityHooked) {
            return
        }
        this._visibilityHooked = true
        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.visibilityState === 'visible') {
                    void this._checkRemoteSwitch(false)
                }
            },
            { passive: true },
        )
    }

    private _readSwitchCache(): { on: boolean; ts: number } | null {
        try {
            const raw = localStorage.getItem(DIAG_SWITCH_CACHE_KEY)
            if (!raw) {
                return null
            }
            const parsed = JSON.parse(raw) as { on?: boolean; ts?: number }
            if (typeof parsed.on !== 'boolean' || typeof parsed.ts !== 'number') {
                return null
            }
            return { on: parsed.on, ts: parsed.ts }
        } catch {
            return null
        }
    }

    private _writeSwitchCache(on: boolean): void {
        try {
            localStorage.setItem(DIAG_SWITCH_CACHE_KEY, JSON.stringify({ on, ts: Date.now() }))
        } catch {
            // localStorage 异常（隐私模式/满额）不影响功能，只是多几次查询
        }
    }

    // ---------- 上报（远程开关模式）/ 本地导出（URL 参数模式 + 上报失败兜底） ----------

    /**
     * 一键上报（按钮点击）：POST 诊断日志到后端入库。
     * 失败（网络/超限/服务端关闭）自动降级为本地导出双通道兜底。
     */
    private async _uploadNow(): Promise<void> {
        // 客户端预裁剪：超服务端 1MB 上限时丢最旧事件，避免白白传一整包被拒
        let events = this.entries
        let payloadStr = ''
        for (;;) {
            payloadStr = JSON.stringify({
                client_type: 'web',
                page_url: location.href.slice(0, 2048),
                user_agent: navigator.userAgent.slice(0, 2048),
                screen: `${window.screen.width}x${window.screen.height} dpr=${window.devicePixelRatio}`.slice(0, 128),
                note: '',
                events,
            })
            if (payloadStr.length <= DIAG_UPLOAD_MAX_BYTES || events.length <= 100) {
                break
            }
            events = events.slice(Math.floor(events.length / 2))
        }
        try {
            const resp = await fetch(DIAG_UPLOAD_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: payloadStr,
            })
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`)
            }
            const json = (await resp.json()) as { success?: boolean; error?: { msg?: string } }
            if (json?.success === false) {
                throw new Error(json.error?.msg || '服务端拒绝')
            }
            this._toast(`诊断日志已上报（${events.length} 条），请联系管理员查看`)
        } catch (err) {
            // 失败兜底：降级本地导出（剪贴板 + 下载双通道），并说明原因
            await this._exportNow(true)
            this._toast(`上报失败（${(err as Error).message}），已降级为本地导出（${events.length} 条）`)
        }
    }

    /**
     * 本地导出：剪贴板 + 下载文件双通道并行（两条路可靠性互补）。
     * 点击即用户手势，满足 clipboard API 权限要求。
     * @param quiet 上报失败兜底调用时为 true（结果并入兜底提示，不单独 toast）
     */
    private async _exportNow(quiet = false): Promise<void> {
        const json = this.exportDiag()
        // 通道 1：剪贴板（优先标准 API，降级 execCommand 兜底旧内核）
        let copied = false
        try {
            await navigator.clipboard.writeText(json)
            copied = true
        } catch {
            try {
                const ta = document.createElement('textarea')
                ta.value = json
                ta.style.position = 'fixed'
                ta.style.opacity = '0'
                document.body.appendChild(ta)
                ta.select()
                copied = document.execCommand('copy')
                ta.remove()
            } catch {
                // ignore：还有下载通道兜底
            }
        }
        // 通道 2：下载 JSON 文件
        let downloaded = false
        try {
            const blob = new Blob([json], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `glx-diag-web-${Date.now()}.json`
            document.body.appendChild(a)
            a.click()
            a.remove()
            setTimeout(() => URL.revokeObjectURL(url), 5000)
            downloaded = true
        } catch {
            // ignore：还有剪贴板通道兜底
        }
        if (!quiet) {
            this._toast(`诊断日志已导出 | 条数=${this.entries.length} | 复制=${copied ? '成功' : '失败'} | 下载=${downloaded ? '成功' : '失败'}`)
        }
    }

    // ---------- 内部实现 ----------

    /** 追加一条记录（超出上限丢最旧） */
    private _push(src: 'console' | 'mark', msg: string): void {
        this.entries.push({
            t: Math.round(performance.now() - this.t0),
            src,
            msg: msg.length > MAX_MSG_LEN ? msg.slice(0, MAX_MSG_LEN) + '…' : msg,
        })
        if (this.entries.length > MAX_ENTRIES) {
            this.entries.splice(0, this.entries.length - MAX_ENTRIES)
        }
    }

    /**
     * hook console.log/warn/error：白名单前缀过滤后收集，再透传原函数
     * （保持 DevTools 正常显示，与其他 console 包装器双向兼容）。
     * 收集路径 try-catch 全包：诊断工具自身故障绝不影响业务日志。
     * enabled=false（远程开关中途关闭）后自动变纯透传，零收集开销。
     */
    private _hookConsole(): void {
        const rawLog = console.log.bind(console)
        const rawWarn = console.warn.bind(console)
        const rawError = console.error.bind(console)
        const collect = (args: unknown[]): void => {
            try {
                if (!this.enabled) {
                    return
                }
                const first = args[0]
                if (typeof first !== 'string') {
                    return
                }
                if (!CONSOLE_WHITELIST_PREFIXES.some((p) => first.startsWith(p))) {
                    return
                }
                this._push('console', args.map((a) => this._stringify(a)).join(' '))
            } catch {
                // ignore：收集失败不影响业务
            }
        }
        console.log = (...args: unknown[]) => {
            collect(args)
            rawLog(...args)
        }
        console.warn = (...args: unknown[]) => {
            collect(args)
            rawWarn(...args)
        }
        console.error = (...args: unknown[]) => {
            collect(args)
            rawError(...args)
        }
    }

    /** 序列化单参数：字符串直传，Error 取 message，其余 JSON 截断 */
    private _stringify(a: unknown): string {
        if (typeof a === 'string') {
            return a
        }
        if (a instanceof Error) {
            return `${a.name}: ${a.message}`
        }
        try {
            const s = JSON.stringify(a)
            return typeof s === 'string' ? s : String(a)
        } catch {
            return String(a)
        }
    }

    /** 注入右上角悬浮按钮（原生 DOM，不依赖 React 挂载时机）。按来源决定行为：remote=上报，url=本地导出 */
    private _mountButton(): void {
        if (this._buttonMounted) {
            return
        }
        this._buttonMounted = true
        const btn = document.createElement('div')
        // 按钮文案随来源：远程开关模式引导上报，URL 参数模式保持本地导出语义
        btn.textContent = this.source === 'remote' ? '上报日志' : '导出诊断'
        Object.assign(btn.style, {
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: String(DIAG_Z_INDEX),
            padding: '6px 10px',
            borderRadius: '14px',
            background: 'rgba(0, 0, 0, 0.55)',
            color: '#fff',
            fontSize: '12px',
            lineHeight: '1',
            userSelect: 'none',
            cursor: 'pointer',
            fontFamily: 'sans-serif',
        } satisfies Partial<CSSStyleDeclaration>)
        btn.addEventListener('click', () => {
            if (this.source === 'remote') {
                void this._uploadNow()
            } else {
                void this._exportNow()
            }
        })
        document.body.appendChild(btn)
        this._buttonEl = btn
    }

    /** 自绘结果提示（3s 消失；不复用业务 Toast，避免引入组件依赖与样式耦合） */
    private _toast(text: string): void {
        const tip = document.createElement('div')
        tip.textContent = text
        Object.assign(tip.style, {
            position: 'fixed',
            top: '56px',
            right: '16px',
            zIndex: String(DIAG_Z_INDEX + 1),
            maxWidth: '80vw',
            padding: '8px 10px',
            borderRadius: '8px',
            background: 'rgba(0, 0, 0, 0.75)',
            color: '#fff',
            fontSize: '12px',
            lineHeight: '1.4',
            fontFamily: 'sans-serif',
        } satisfies Partial<CSSStyleDeclaration>)
        document.body.appendChild(tip)
        setTimeout(() => tip.remove(), 3000)
    }
}

/** 全局单例（与 ai_mobile diag 同模式） */
export const diag = new DiagCollector()
